import { useState, type ReactNode } from 'react';
import {
  Box,
  Typography,
  Card,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Alert,
  Stack,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  AccountBalance as TransferIcon,
  AccountBalanceWallet as WalletIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  getAdminUsers, 
  getAdminUserDetails,
  updateAdminUser, 
  getUserTransferDetails,
  createUserTransferDetail,
  createUserSteemWallet,
  createUserHiveWallet,
  deleteUserTransferDetail,
  getAdminPairs,
} from '@/services/api';
import { SectionHeader } from '@/components/common/Page';
import ExchangeLogo from '@/components/common/ExchangeLogo';
import { useAdminI18n } from '@/contexts/AdminI18nContext';
import { useAdminTime } from '@/contexts/AdminTimeContext';
import { formatUsd } from '@/utils/format';
import type { AdminUser, UserExchange, UserTransferDetail, UserWallet, ArbitragePair, Circle } from '@/types';

const MAX_ADMIN_NOTE_LENGTH = 280;
const sectionSx = {
  border: '1px solid rgba(226, 255, 238, 0.12)',
  borderRadius: 1,
  backgroundColor: 'rgba(244, 255, 249, 0.035)',
  p: { xs: 1.5, sm: 2 },
};

const detailGridSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, minmax(0, 1fr))',
    md: 'repeat(4, minmax(0, 1fr))',
  },
  gap: 1.35,
};

const splitGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
  gap: 1.5,
};

const fieldGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
  gap: 1.25,
};

interface TransferFormState {
  id: number | null;
  pair_id: number | '';
  deposit_address: string;
  memo: string;
  network: string;
  notes: string;
  steem_account_name: string;
  hive_account_name: string;
}

const emptyTransferForm: TransferFormState = {
  id: null,
  pair_id: '',
  deposit_address: '',
  memo: '',
  network: '',
  notes: '',
  steem_account_name: '',
  hive_account_name: '',
};

function formatPairCoin(pair: Pick<ArbitragePair, 'pair_type' | 'coin_from' | 'coin_to'>) {
  return pair.pair_type === 'double' && pair.coin_to ? `${pair.coin_from} -> ${pair.coin_to}` : pair.coin_from;
}

function formatPairRoute(
  pair: {
    exchange_buy: string;
    exchange_sell: string;
    exchange_buy_name?: string | null;
    exchange_sell_name?: string | null;
  }
) {
  return `${pair.exchange_buy_name || pair.exchange_buy} -> ${pair.exchange_sell_name || pair.exchange_sell}`;
}

function pairContainsCoin(pair: Pick<ArbitragePair, 'coin_from' | 'coin_to'> | undefined, coin: string) {
  if (!pair) return false;
  const target = coin.toUpperCase();
  return pair.coin_from?.toUpperCase() === target || pair.coin_to?.toUpperCase() === target;
}

function formatTransferCoin(transfer: UserTransferDetail) {
  return transfer.pair_type === 'double' && transfer.coin_to
    ? `${transfer.coin_from || transfer.coin} -> ${transfer.coin_to}`
    : transfer.coin_from || transfer.coin;
}

function formatTransferRoute(transfer: UserTransferDetail) {
  return `${transfer.exchange_buy_name || transfer.exchange_buy || 'Source'} -> ${
    transfer.exchange_sell_name || transfer.exchange_sell || transfer.exchange_name || transfer.exchange_id
  }`;
}

function formatWalletPair(wallet: UserWallet) {
  if (!wallet.pair_id || !wallet.coin_from) return '-';
  return wallet.pair_type === 'double' && wallet.coin_to ? `${wallet.coin_from} -> ${wallet.coin_to}` : wallet.coin_from;
}

function formatWalletRoute(wallet: UserWallet) {
  if (!wallet.exchange_buy && !wallet.exchange_sell) return '-';
  return `${wallet.exchange_buy_name || wallet.exchange_buy || 'Source'} -> ${
    wallet.exchange_sell_name || wallet.exchange_sell || 'Destination'
  }`;
}

function formatUserName(user: Pick<AdminUser, 'first_name' | 'last_name' | 'telegram_username' | 'telegram_id'>) {
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  return name || user.telegram_username || user.telegram_id;
}

function formatCircleCoin(circle: Pick<Circle, 'pair_type' | 'coin_from' | 'coin_to'>) {
  return circle.pair_type === 'double' && circle.coin_to ? `${circle.coin_from} -> ${circle.coin_to}` : circle.coin_from || '-';
}

function formatCircleRoute(circle: Pick<Circle, 'exchange_buy' | 'exchange_sell'>) {
  return `${circle.exchange_buy || 'Source'} -> ${circle.exchange_sell || 'Destination'}`;
}

function formatPercent(value: number | null | undefined) {
  return `${Number(value || 0).toFixed(2)}%`;
}

function getExchangeStatusColor(status: UserExchange['verification_status']) {
  if (status === 'verified') return 'success';
  if (status === 'pending') return 'warning';
  if (status === 'rejected') return 'error';
  return 'default';
}

function DetailItem({ label, value }: { label: string; value: ReactNode }) {
  const displayValue = value === null || value === undefined || value === '' ? '-' : value;

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 800, lineHeight: 1.25 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.35, lineHeight: 1.35, overflowWrap: 'anywhere' }}>
        {displayValue}
      </Typography>
    </Box>
  );
}

function InfoSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box sx={sectionSx}>
      <Typography variant="subtitle2" sx={{ mb: 1.25 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function TransferUserContext({
  user,
  exchanges,
  loading,
}: {
  user: AdminUser | null;
  exchanges: UserExchange[];
  loading: boolean;
}) {
  const { t } = useAdminI18n();

  if (!user) return null;

  const statusLabels = {
    pending: t('admin.common.pending'),
    verified: t('admin.common.verified'),
    rejected: t('admin.common.rejected'),
  };
  const actionableExchanges = exchanges.filter((exchange) =>
    ['verified', 'pending'].includes(exchange.verification_status)
  );
  const verifiedCount = actionableExchanges.filter(
    (exchange) => exchange.verification_status === 'verified'
  ).length;
  const pendingCount = actionableExchanges.filter(
    (exchange) => exchange.verification_status === 'pending'
  ).length;

  return (
    <Box sx={{ ...sectionSx, mb: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        justifyContent="space-between"
        spacing={1.25}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6">{formatUserName(user)}</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {user.telegram_username ? `@${user.telegram_username}` : t('admin.users.noUsername')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('admin.verifications.telegramId', { id: user.telegram_id })}
            </Typography>
          </Stack>
        </Box>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Chip size="small" color="success" label={t('admin.users.verifiedCount', { count: verifiedCount })} />
          <Chip size="small" color="warning" variant="outlined" label={t('admin.users.pendingCount', { count: pendingCount })} />
        </Stack>
      </Stack>

      <Divider sx={{ my: 1.5 }} />

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 800 }}>
        {t('admin.users.userContextVerifiedPending')}
      </Typography>

      {loading ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            {t('admin.users.loadingExchangeUids')}
          </Typography>
        </Stack>
      ) : actionableExchanges.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('admin.users.noActionableExchangeUids')}
        </Typography>
      ) : (
        <Stack spacing={0.75}>
          {actionableExchanges.map((exchange) => {
            const exchangeName = exchange.exchange_name || exchange.exchange_id;

            return (
              <Box
                key={exchange.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'minmax(180px, 1.2fr) minmax(150px, 1fr) auto',
                  },
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  borderRadius: 1,
                  backgroundColor: 'rgba(244, 255, 249, 0.035)',
                  border: '1px solid rgba(226, 255, 238, 0.10)',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                  <ExchangeLogo src={exchange.logo_url} name={exchangeName} id={exchange.exchange_id} size={24} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      {exchangeName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {exchange.exchange_id}
                    </Typography>
                  </Box>
                </Stack>
                <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                  {t('admin.verifications.uid', { uid: exchange.exchange_uid })}
                </Typography>
                <Chip
                  size="small"
                  color={getExchangeStatusColor(exchange.verification_status)}
                  label={statusLabels[exchange.verification_status]}
                  variant={exchange.verification_status === 'verified' ? 'filled' : 'outlined'}
                  sx={{ justifySelf: { xs: 'flex-start', sm: 'end' } }}
                />
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}

export default function AdminUsers() {
  const queryClient = useQueryClient();
  const { t, formatNumber } = useAdminI18n();
  const { formatDateTime } = useAdminTime();
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [detailsUserId, setDetailsUserId] = useState<number | null>(null);
  const [userDialog, setUserDialog] = useState<{
    open: boolean;
    userId: number | null;
    level: 'basic' | 'private';
    notes: string;
  }>({
    open: false,
    userId: null,
    level: 'basic',
    notes: '',
  });
  const [transferDialog, setTransferDialog] = useState(false);
  const [newTransfer, setNewTransfer] = useState<TransferFormState>(emptyTransferForm);

  const { data: usersData, isLoading } = useQuery({
    queryKey: ['admin-users', search],
    queryFn: () => getAdminUsers(50, 0, search || undefined),
  });

  const { data: pairsData } = useQuery({
    queryKey: ['admin-pairs'],
    queryFn: getAdminPairs,
  });

  const { data: transfersData, refetch: refetchTransfers } = useQuery({
    queryKey: ['user-transfers', selectedUser?.id],
    queryFn: () => getUserTransferDetails(selectedUser!.id),
    enabled: !!selectedUser,
  });

  const { data: transferUserDetails, isLoading: isTransferUserDetailsLoading } = useQuery({
    queryKey: ['admin-user-details', selectedUser?.id],
    queryFn: () => getAdminUserDetails(selectedUser!.id),
    enabled: !!selectedUser,
  });

  const { data: userDetails, isLoading: isDetailsLoading } = useQuery({
    queryKey: ['admin-user-details', detailsUserId],
    queryFn: () => getAdminUserDetails(detailsUserId!),
    enabled: detailsUserId !== null,
  });

  const userMutation = useMutation({
    mutationFn: ({ userId, level, notes }: { userId: number; level: 'basic' | 'private'; notes: string }) =>
      updateAdminUser(userId, { access_level: level, admin_notes: notes }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-user-details', variables.userId] });
      setUserDialog({ open: false, userId: null, level: 'basic', notes: '' });
    },
  });

  const createTransferMutation = useMutation({
    mutationFn: (data: TransferFormState) =>
      createUserTransferDetail(selectedUser!.id, {
        pair_id: data.pair_id,
        deposit_address: data.deposit_address,
        memo: data.memo,
        network: data.network,
        notes: data.notes,
      }),
    onSuccess: () => {
      refetchTransfers();
      if (selectedUser) {
        queryClient.invalidateQueries({ queryKey: ['admin-user-details', selectedUser.id] });
      }
      setTransferDialog(false);
      setNewTransfer(emptyTransferForm);
    },
  });

  const createSteemWalletMutation = useMutation({
    mutationFn: (data: TransferFormState) =>
      createUserSteemWallet(selectedUser!.id, {
        pair_id: data.pair_id,
        account_name: data.steem_account_name,
        network: 'STEEM',
        notes: data.notes,
      }),
    onSuccess: () => {
      refetchTransfers();
      if (selectedUser) {
        queryClient.invalidateQueries({ queryKey: ['admin-user-details', selectedUser.id] });
      }
      setTransferDialog(false);
      setNewTransfer(emptyTransferForm);
    },
  });

  const createHiveWalletMutation = useMutation({
    mutationFn: (data: TransferFormState) =>
      createUserHiveWallet(selectedUser!.id, {
        pair_id: data.pair_id,
        account_name: data.hive_account_name,
        network: 'HIVE',
        notes: data.notes,
      }),
    onSuccess: () => {
      refetchTransfers();
      if (selectedUser) {
        queryClient.invalidateQueries({ queryKey: ['admin-user-details', selectedUser.id] });
      }
      setTransferDialog(false);
      setNewTransfer(emptyTransferForm);
    },
  });

  const deleteTransferMutation = useMutation({
    mutationFn: (transferId: number) => deleteUserTransferDetail(selectedUser!.id, transferId),
    onSuccess: () => {
      refetchTransfers();
      if (selectedUser) {
        queryClient.invalidateQueries({ queryKey: ['admin-user-details', selectedUser.id] });
      }
    },
  });

  const handleOpenUserDialog = (user: AdminUser) => {
    setUserDialog({
      open: true,
      userId: user.id,
      level: user.access_level,
      notes: user.admin_notes || '',
    });
  };

  const handleOpenTransfers = (user: AdminUser) => {
    setSelectedUser(user);
  };

  const handleOpenDetails = (user: AdminUser) => {
    setDetailsUserId(user.id);
  };

  const handleOpenTransferDialog = (transfer?: UserTransferDetail) => {
    setNewTransfer(
      transfer
        ? {
            id: transfer.id,
            pair_id: transfer.pair_id || '',
            deposit_address: transfer.deposit_address || '',
            memo: transfer.memo || '',
            network: transfer.network || '',
            notes: transfer.notes || '',
            steem_account_name: '',
            hive_account_name: '',
          }
        : emptyTransferForm
    );
    setTransferDialog(true);
  };

  const handleCloseTransferDialog = () => {
    setTransferDialog(false);
    setNewTransfer(emptyTransferForm);
  };

  const selectedPair = pairsData?.pairs.find((pair) => pair.id === Number(newTransfer.pair_id));
  const canCreateSteemWallet = !newTransfer.id && pairContainsCoin(selectedPair, 'STEEM');
  const canCreateHiveWallet = !newTransfer.id && pairContainsCoin(selectedPair, 'HIVE');
  const transferDialogUser = transferUserDetails?.user || selectedUser;
  const transferDialogExchanges = transferUserDetails?.exchanges || [];
  const statusLabels = {
    pending: t('admin.common.pending'),
    verified: t('admin.common.verified'),
    rejected: t('admin.common.rejected'),
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <Box>
      <SectionHeader title={t('admin.users.title')} detail={t('admin.common.total', { count: usersData?.total ?? 0 })} />

      <TextField
        fullWidth
        size="small"
        placeholder={t('admin.users.search')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      <TableContainer component={Card}>
        <Table size="small" sx={{ minWidth: 960 }}>
          <TableHead>
            <TableRow>
              <TableCell>{t('admin.users.user')}</TableCell>
              <TableCell>{t('admin.common.access')}</TableCell>
              <TableCell>{t('admin.users.notes')}</TableCell>
              <TableCell>{t('admin.common.exchanges')}</TableCell>
              <TableCell>{t('admin.users.rounds')}</TableCell>
              <TableCell>{t('admin.common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {usersData?.users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <Typography variant="body2">
                    {user.first_name} {user.last_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user.telegram_username
                      ? `@${user.telegram_username}`
                      : t('admin.verifications.telegramId', { id: user.telegram_id })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={user.access_level === 'private' ? t('admin.common.private') : t('admin.common.basic')}
                    color={user.access_level === 'private' ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 280 }}>
                  {user.admin_notes ? (
                    <Tooltip title={user.admin_notes}>
                      <Typography
                        variant="body2"
                        sx={{
                          display: '-webkit-box',
                          overflow: 'hidden',
                          WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: 2,
                        }}
                      >
                        {user.admin_notes}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.62 }}>
                      {t('admin.common.noNote')}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{user.verified_exchanges || 0}</TableCell>
                <TableCell>{user.total_circles || 0}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title={t('admin.users.viewInfo')}>
                      <IconButton size="small" onClick={() => handleOpenDetails(user)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('admin.users.editAccessNotes')}>
                      <IconButton size="small" onClick={() => handleOpenUserDialog(user)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('admin.users.configureTransferDetails')}>
                      <IconButton size="small" onClick={() => handleOpenTransfers(user)}>
                        <TransferIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Full User Info Dialog */}
      <Dialog
        open={detailsUserId !== null}
        onClose={() => setDetailsUserId(null)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            width: 'min(1040px, calc(100vw - 24px))',
            maxHeight: 'calc(100dvh - 32px)',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 1.75,
            borderBottom: '1px solid rgba(226, 255, 238, 0.12)',
          }}
        >
          {t('admin.users.userInfoTitle')}
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {isDetailsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : userDetails ? (
            <Stack spacing={1.5} sx={{ p: { xs: 2, sm: 2.5 } }}>
              <Box
                sx={{
                  ...sectionSx,
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: 1.5,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h5">{formatUserName(userDetails.user)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {userDetails.user.telegram_username
                      ? `@${userDetails.user.telegram_username}`
                      : t('admin.users.noUsername')}{' '}
                    | {t('admin.verifications.telegramId', { id: userDetails.user.telegram_id })}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label={
                      userDetails.user.access_level === 'private'
                        ? t('admin.common.private')
                        : t('admin.common.basic')
                    }
                    color={userDetails.user.access_level === 'private' ? 'success' : 'default'}
                  />
                  <Chip
                    size="small"
                    label={
                      userDetails.user.onboarding_completed
                        ? t('admin.users.onboarded')
                        : t('admin.users.notOnboarded')
                    }
                    color={userDetails.user.onboarding_completed ? 'success' : 'warning'}
                    variant="outlined"
                  />
                </Stack>
              </Box>

              <InfoSection title={t('admin.users.activity')}>
                <Box sx={detailGridSx}>
                  <DetailItem label={t('admin.users.firstActivity')} value={formatDateTime(userDetails.activity.first_activity_at)} />
                  <DetailItem label={t('admin.users.lastActivity')} value={formatDateTime(userDetails.activity.last_activity_at)} />
                  <DetailItem label={t('admin.users.lastSeen')} value={formatDateTime(userDetails.activity.last_seen_at)} />
                  <DetailItem label={t('admin.users.profileUpdated')} value={formatDateTime(userDetails.activity.profile_updated_at)} />
                  <DetailItem label={t('admin.users.lastRound')} value={formatDateTime(userDetails.activity.last_round_at)} />
                  <DetailItem
                    label={t('admin.users.lastExchangeSubmission')}
                    value={formatDateTime(userDetails.activity.last_exchange_submission_at)}
                  />
                  <DetailItem
                    label={t('admin.users.lastExchangeDecision')}
                    value={formatDateTime(userDetails.activity.last_exchange_decision_at)}
                  />
                  <DetailItem label={t('admin.users.lastPairNote')} value={formatDateTime(userDetails.activity.last_pair_note_at)} />
                  <DetailItem
                    label={t('admin.users.lastAnnouncementRead')}
                    value={formatDateTime(userDetails.activity.last_announcement_read_at)}
                  />
                  <DetailItem
                    label={t('admin.users.lastTransferUpdate')}
                    value={formatDateTime(userDetails.activity.last_transfer_update_at)}
                  />
                  <DetailItem
                    label={t('admin.users.lastWalletCreated')}
                    value={formatDateTime(userDetails.activity.last_wallet_created_at)}
                  />
                </Box>
              </InfoSection>

              <InfoSection title={t('admin.users.profileAndStats')}>
                <Box sx={splitGridSx}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 800 }}>
                      {t('admin.users.profile')}
                    </Typography>
                    <Box sx={fieldGridSx}>
                        <DetailItem label={t('admin.users.telegramId')} value={userDetails.user.telegram_id} />
                        <DetailItem label={t('admin.users.username')} value={userDetails.user.telegram_username ? `@${userDetails.user.telegram_username}` : '-'} />
                        <DetailItem label={t('admin.users.firstName')} value={userDetails.user.first_name} />
                        <DetailItem label={t('admin.users.lastName')} value={userDetails.user.last_name} />
                        <DetailItem label={t('admin.users.selfReportedBudget')} value={formatUsd(userDetails.user.budget || 0)} />
                        <DetailItem label={t('admin.users.monthlyProfitGoal')} value={formatUsd(userDetails.user.monthly_profit_goal || 0)} />
                        <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                          <DetailItem label={t('admin.users.adminNotes')} value={userDetails.user.admin_notes || t('admin.common.noNote')} />
                        </Box>
                    </Box>
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 800 }}>
                      {t('admin.users.stats')}
                    </Typography>
                    <Box sx={fieldGridSx}>
                      <DetailItem label={t('admin.users.rounds')} value={formatNumber(userDetails.stats.total_circles)} />
                      <DetailItem label={t('admin.users.totalProfit')} value={formatUsd(userDetails.stats.total_profit)} />
                      <DetailItem label={t('admin.users.profit30d')} value={formatUsd(userDetails.stats.last_30_days_profit)} />
                      <DetailItem label={t('admin.users.avgProfit')} value={formatPercent(userDetails.stats.average_profit_percent)} />
                      <DetailItem label={t('admin.users.totalExchanges')} value={formatNumber(userDetails.stats.total_exchanges)} />
                      <DetailItem label={t('admin.users.verifiedExchanges')} value={formatNumber(userDetails.stats.verified_exchanges)} />
                      <DetailItem label={t('admin.users.pendingExchanges')} value={formatNumber(userDetails.stats.pending_exchanges)} />
                      <DetailItem label={t('admin.users.rejectedExchanges')} value={formatNumber(userDetails.stats.rejected_exchanges)} />
                      <DetailItem label={t('admin.users.transferDetailsCount')} value={formatNumber(userDetails.stats.transfer_details)} />
                      <DetailItem label={t('admin.users.walletsCount')} value={formatNumber(userDetails.stats.wallet_count)} />
                      <DetailItem label={t('admin.users.pairNotesCount')} value={formatNumber(userDetails.stats.pair_notes)} />
                      <DetailItem label={t('admin.users.readAnnouncements')} value={formatNumber(userDetails.stats.read_announcements)} />
                    </Box>
                  </Box>
                </Box>
              </InfoSection>

              <InfoSection title={t('admin.users.exchangeVerifications')}>
                {userDetails.exchanges.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('admin.users.noExchangeVerifications')}
                  </Typography>
                ) : (
                  <Stack divider={<Divider flexItem />} spacing={1}>
                    {userDetails.exchanges.map((exchange) => (
                      <Stack
                        key={exchange.id}
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2">
                            {exchange.exchange_name || exchange.exchange_id}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('admin.verifications.uid', { uid: exchange.exchange_uid })}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Chip size="small" label={statusLabels[exchange.verification_status]} />
                          <Typography variant="caption" color="text.secondary">
                            {formatDateTime(exchange.created_at)}
                          </Typography>
                        </Stack>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </InfoSection>

              <InfoSection title={t('admin.users.recentRounds')}>
                {userDetails.recent_circles.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('admin.users.noRounds')}
                  </Typography>
                ) : (
                  <Stack divider={<Divider flexItem />} spacing={1}>
                    {userDetails.recent_circles.map((circle) => (
                      <Stack
                        key={circle.id}
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2">
                            {formatCircleCoin(circle)} {' | '} {formatCircleRoute(circle)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {circle.notes || t('admin.common.noNote')}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, minWidth: 0 }}>
                          <Typography variant="body2">
                            {formatUsd(circle.amount_invested)} {'->'} {formatUsd(circle.amount_received)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatPercent(circle.profit_percent)} {' | '} {formatDateTime(circle.created_at)}
                          </Typography>
                        </Box>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </InfoSection>

              <InfoSection title={t('admin.users.wallets')}>
                {userDetails.wallets.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('admin.users.noWallets')}
                  </Typography>
                ) : (
                  <Stack divider={<Divider flexItem />} spacing={1}>
                    {userDetails.wallets.map((wallet) => (
                      <Stack
                        key={wallet.id}
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Chip size="small" label={wallet.network} />
                            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                              {wallet.account}
                            </Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {wallet.provider}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {formatWalletPair(wallet)} {' | '} {formatWalletRoute(wallet)}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {wallet.created_by_username ? `@${wallet.created_by_username}` : '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDateTime(wallet.created_at)}
                          </Typography>
                        </Box>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </InfoSection>

              <Box sx={splitGridSx}>
                <InfoSection title={t('admin.users.transferDetails')}>
                  {userDetails.transfers.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {t('admin.users.noTransferDetailsConfigured')}
                    </Typography>
                  ) : (
                    <Stack divider={<Divider flexItem />} spacing={1}>
                      {userDetails.transfers.map((transfer) => (
                        <Box key={transfer.id} sx={{ minWidth: 0 }}>
                          <Typography variant="body2">
                            {formatTransferCoin(transfer)} {' | '} {formatTransferRoute(transfer)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                            {t('admin.users.networkAddress', {
                              network: transfer.network || '-',
                              address: transfer.deposit_address || '-',
                            })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('admin.users.memo', { memo: transfer.memo || '-' })}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </InfoSection>
                <InfoSection title={t('admin.users.pairNotes')}>
                  {userDetails.pair_notes.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {t('admin.users.noPairNotes')}
                    </Typography>
                  ) : (
                    <Stack divider={<Divider flexItem />} spacing={1}>
                      {userDetails.pair_notes.map((note) => (
                        <Box key={note.id} sx={{ minWidth: 0 }}>
                          <Typography variant="body2">
                            {formatPairCoin(note)} {' | '} {formatPairRoute(note)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                            {note.note || t('admin.common.noNote')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('admin.users.updated', { date: formatDateTime(note.updated_at) })}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </InfoSection>
              </Box>
            </Stack>
          ) : (
            <Box sx={{ p: 2.5 }}>
              <Alert severity="error">{t('admin.users.failedToLoadDetails')}</Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 1.25,
            borderTop: '1px solid rgba(226, 255, 238, 0.12)',
            backgroundColor: 'rgba(16, 23, 21, 0.98)',
          }}
        >
          <Button onClick={() => setDetailsUserId(null)}>{t('admin.common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* User Details Dialog */}
      <Dialog open={userDialog.open} onClose={() => setUserDialog({ ...userDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>{t('admin.users.editUser')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>{t('admin.users.accessLevel')}</InputLabel>
            <Select
              value={userDialog.level}
              label={t('admin.users.accessLevel')}
              onChange={(e) => setUserDialog({ ...userDialog, level: e.target.value as 'basic' | 'private' })}
            >
              <MenuItem value="basic">{t('admin.common.basic')}</MenuItem>
              <MenuItem value="private">{t('admin.common.private')}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label={t('admin.users.notes')}
            placeholder={t('admin.users.notesPlaceholder')}
            value={userDialog.notes}
            onChange={(e) =>
              setUserDialog({ ...userDialog, notes: e.target.value.slice(0, MAX_ADMIN_NOTE_LENGTH) })
            }
            helperText={`${userDialog.notes.length}/${MAX_ADMIN_NOTE_LENGTH}`}
            multiline
            minRows={3}
            maxRows={5}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialog({ ...userDialog, open: false })}>{t('admin.common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={() =>
              userMutation.mutate({
                userId: userDialog.userId!,
                level: userDialog.level,
                notes: userDialog.notes,
              })
            }
            disabled={userMutation.isPending}
          >
            {t('admin.common.update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Details Dialog */}
      <Dialog open={!!selectedUser} onClose={() => setSelectedUser(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {t('admin.users.transferDialogTitle', {
            name: selectedUser ? formatUserName(selectedUser) : '',
          })}
        </DialogTitle>
        <DialogContent>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleOpenTransferDialog()}
            sx={{ mb: 2 }}
          >
            {t('admin.users.addPairDetails')}
          </Button>

          {(transfersData?.transfers.length ?? 0) === 0 ? (
            <Alert severity="info">{t('admin.users.noPairTransferDetails')}</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('admin.users.pair')}</TableCell>
                  <TableCell>{t('admin.common.destination')}</TableCell>
                  <TableCell>{t('admin.common.network')}</TableCell>
                  <TableCell>{t('admin.users.address')}</TableCell>
                  <TableCell>{t('admin.users.memoLabel')}</TableCell>
                  <TableCell>{t('admin.common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transfersData?.transfers.map((transfer: UserTransferDetail) => (
                  <TableRow key={transfer.id}>
                    <TableCell>
                      <Typography variant="body2">{formatTransferCoin(transfer)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatTransferRoute(transfer)}
                      </Typography>
                    </TableCell>
                    <TableCell>{transfer.exchange_name || transfer.exchange_sell_name || transfer.exchange_id}</TableCell>
                    <TableCell>{transfer.network || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {transfer.deposit_address || '-'}
                    </TableCell>
                    <TableCell>{transfer.memo || '-'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title={t('admin.users.editDetails')}>
                          <IconButton size="small" onClick={() => handleOpenTransferDialog(transfer)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('admin.users.deleteDetails')}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => deleteTransferMutation.mutate(transfer.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedUser(null)}>{t('admin.common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Add Transfer Dialog */}
      <Dialog open={transferDialog} onClose={handleCloseTransferDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {newTransfer.id ? t('admin.users.editTransferTitle') : t('admin.users.addTransferTitle')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TransferUserContext
              user={transferDialogUser}
              exchanges={transferDialogExchanges}
              loading={isTransferUserDetailsLoading}
            />
            <FormControl fullWidth>
              <InputLabel>{t('admin.users.pair')}</InputLabel>
              <Select
                value={newTransfer.pair_id}
                label={t('admin.users.pair')}
                onChange={(e) => setNewTransfer({ ...newTransfer, pair_id: e.target.value as number | '' })}
              >
                {pairsData?.pairs.map((pair: ArbitragePair) => (
                  <MenuItem key={pair.id} value={pair.id}>
                    {formatPairCoin(pair)} - {formatPairRoute(pair)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedPair && (
              <Alert severity="info">
                {t('admin.users.destinationHint', {
                  exchange: selectedPair.exchange_sell_name || selectedPair.exchange_sell,
                  coin: selectedPair.coin_from,
                })}
              </Alert>
            )}
            {canCreateSteemWallet && (
              <>
                <Alert
                  severity="info"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      startIcon={<WalletIcon />}
                      disabled={
                        createSteemWalletMutation.isPending ||
                        createHiveWalletMutation.isPending ||
                        !newTransfer.steem_account_name
                      }
                      onClick={() => createSteemWalletMutation.mutate(newTransfer)}
                    >
                      {createSteemWalletMutation.isPending
                        ? t('admin.users.creatingSteemWallet')
                        : t('admin.users.createSteemWallet')}
                    </Button>
                  }
                >
                  {t('admin.users.createSteemWalletHint')}
                </Alert>
                {createSteemWalletMutation.error && (
                  <Alert severity="error">
                    {createSteemWalletMutation.error instanceof Error
                      ? createSteemWalletMutation.error.message
                      : t('admin.users.createSteemWalletError')}
                  </Alert>
                )}
                <TextField
                  label={t('admin.users.steemAccountName')}
                  placeholder={t('admin.users.steemAccountNamePlaceholder')}
                  value={newTransfer.steem_account_name}
                  helperText={t('admin.users.steemAccountNameHelp')}
                  onChange={(e) =>
                    setNewTransfer({
                      ...newTransfer,
                      steem_account_name: e.target.value.toLowerCase(),
                    })
                  }
                />
              </>
            )}
            {canCreateHiveWallet && (
              <>
                <Alert
                  severity="info"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      startIcon={<WalletIcon />}
                      disabled={
                        createHiveWalletMutation.isPending ||
                        createSteemWalletMutation.isPending ||
                        !newTransfer.hive_account_name
                      }
                      onClick={() => createHiveWalletMutation.mutate(newTransfer)}
                    >
                      {createHiveWalletMutation.isPending
                        ? t('admin.users.creatingHiveWallet')
                        : t('admin.users.createHiveWallet')}
                    </Button>
                  }
                >
                  {t('admin.users.createHiveWalletHint')}
                </Alert>
                {createHiveWalletMutation.error && (
                  <Alert severity="error">
                    {createHiveWalletMutation.error instanceof Error
                      ? createHiveWalletMutation.error.message
                      : t('admin.users.createHiveWalletError')}
                  </Alert>
                )}
                <TextField
                  label={t('admin.users.hiveAccountName')}
                  placeholder={t('admin.users.hiveAccountNamePlaceholder')}
                  value={newTransfer.hive_account_name}
                  helperText={t('admin.users.hiveAccountNameHelp')}
                  onChange={(e) =>
                    setNewTransfer({
                      ...newTransfer,
                      hive_account_name: e.target.value.toLowerCase(),
                    })
                  }
                />
              </>
            )}
            <TextField
              label={t('admin.common.network')}
              placeholder={t('admin.users.networkPlaceholder')}
              value={newTransfer.network}
              onChange={(e) => setNewTransfer({ ...newTransfer, network: e.target.value })}
            />
            <TextField
              label={t('admin.users.depositAddress')}
              placeholder={t('admin.users.addressPlaceholder')}
              value={newTransfer.deposit_address}
              onChange={(e) => setNewTransfer({ ...newTransfer, deposit_address: e.target.value })}
            />
            <TextField
              label={t('admin.users.memoTag')}
              placeholder={t('admin.users.memoPlaceholder')}
              value={newTransfer.memo}
              onChange={(e) => setNewTransfer({ ...newTransfer, memo: e.target.value })}
            />
            <TextField
              label={t('admin.users.notes')}
              value={newTransfer.notes}
              onChange={(e) => setNewTransfer({ ...newTransfer, notes: e.target.value })}
              multiline
              minRows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTransferDialog}>{t('admin.common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => createTransferMutation.mutate(newTransfer)}
            disabled={createTransferMutation.isPending || !newTransfer.pair_id}
          >
            {t('admin.common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
